// Step 2: map source columns to target fields.
// Auto-maps on entry (learned mapping if this header shape was seen before, else similarity),
// enforces the required fields (Product, Length, Location), keeps one source column per field,
// and remembers the confirmed mapping for the organization. Unmapped columns are ignored.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { useRememberColumnMapping } from '~/pages/inventory-import/api/useColumnMappings';
import {
  headerSignature,
  seededMap,
  type LearnedStore,
} from '~/pages/inventory-import/lib/autoMap';
import {
  missingRequiredFields,
  SELLER_FIELDS,
} from '~/pages/inventory-import/lib/targetFields';
import { sellerFieldLabel } from '~/pages/inventory-import/lib/fieldLabel';
import type {
  ColumnMapping,
  ParsedFile,
} from '~/pages/inventory-import/lib/types';

interface MappingStepProps {
  file: ParsedFile;
  learned: LearnedStore;
  proposed?: ColumnMapping;
  onBack: () => void;
  onConfirm: (mapping: ColumnMapping) => void;
}

const REQUIRED_FIELDS = SELLER_FIELDS.filter((f) => f.required);

export function MappingStep({
  file,
  learned,
  proposed,
  onBack,
  onConfirm,
}: MappingStepProps) {
  const { t } = useTranslation('inventory-import');
  const rememberMapping = useRememberColumnMapping();
  const initial = useMemo(
    () => seededMap(file, learned, proposed),
    [file, learned, proposed]
  );
  const [mapping, setMapping] = useState<ColumnMapping>(initial.mapping);
  // Track in state (not from the memo) so "reset learned" can clear the banner — the memo is keyed
  // on `file`, which doesn't change when we drop the learned mapping.
  const [fromMemory, setFromMemory] = useState(initial.fromMemory);

  // Up to 3 distinct non-empty sample values per column (so repeats aren't shown), computed once
  // per file instead of on every render. Use an empty-string check rather than truthiness so a
  // legitimate "0" still shows.
  const samplesByHeader = useMemo(() => {
    const samples: Record<string, string> = {};
    for (const header of file.headers) {
      const seen = new Set<string>();
      for (const row of file.rows) {
        const value = (row[header] ?? '').trim();
        if (value !== '' && !seen.has(value)) {
          seen.add(value);
          if (seen.size >= 3) break;
        }
      }
      samples[header] = [...seen].join(', ');
    }
    return samples;
  }, [file]);

  const headerForField = (key: string) =>
    file.headers.find((h) => mapping[h] === key) ?? '';
  const missingRequired = missingRequiredFields(Object.values(mapping));
  const ignoredCount = file.headers.filter((h) => !mapping[h]).length;
  const canContinue = missingRequired.length === 0;

  // Assign a field to a column; one source per field (clear the field from any other column).
  const setHeader = (header: string, key: string) =>
    setMapping((m) => {
      const next: ColumnMapping = { ...m, [header]: key };
      if (key) {
        for (const h of Object.keys(next)) {
          if (h !== header && next[h] === key) next[h] = '';
        }
      }
      return next;
    });

  return (
    <div className="flex flex-col gap-4">
      {fromMemory && (
        <p className="rounded-lg bg-status-info-50 px-3 py-2 text-sm text-status-info-700">
          {t('mapping.from_memory')}
        </p>
      )}

      {/* Required-field checklist */}
      <div className="rounded-lg border border-neut-border bg-neut-bg-alt p-4">
        <p className="mb-2 text-sm font-medium text-neut-prim">
          {t('mapping.required_fields')}
        </p>
        <div className="flex flex-wrap gap-2">
          {REQUIRED_FIELDS.map((f) => {
            const src = headerForField(f.key);
            return (
              <span
                key={f.key}
                className={`rounded-md px-2 py-1 text-xs font-medium ${
                  src
                    ? 'bg-success-100 text-success-700'
                    : 'bg-error-100 text-error-600'
                }`}
              >
                {sellerFieldLabel(t, f.key)}:{' '}
                {src
                  ? t('mapping.mapped_from', { source: src })
                  : t('mapping.not_mapped')}
              </span>
            );
          })}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neut-border">
        <table className="w-full text-sm">
          <thead className="bg-neut-bg text-left text-neut-sec">
            <tr>
              <th className="px-3 py-2 font-medium">
                {t('mapping.table.source_column')}
              </th>
              <th className="px-3 py-2 font-medium">
                {t('mapping.table.sample_values')}
              </th>
              <th className="px-3 py-2 font-medium">
                {t('mapping.table.maps_to')}
              </th>
            </tr>
          </thead>
          <tbody>
            {file.headers.map((header) => {
              const ignored = !mapping[header];
              return (
                <tr key={header} className="border-t border-neut-border">
                  <td className="px-3 py-2 font-medium text-neut-prim">
                    {header}
                  </td>
                  <td className="px-3 py-2 text-neut-sec">
                    {samplesByHeader[header] || (
                      <em>{t('mapping.table.empty')}</em>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      aria-label={t('mapping.table.map_column_aria', {
                        header,
                      })}
                      value={mapping[header] ?? ''}
                      onChange={(e) => setHeader(header, e.target.value)}
                      className={`rounded-md border px-2 py-1 text-sm ${
                        ignored
                          ? 'border-neut-border text-neut-sec'
                          : 'border-prim text-neut-prim'
                      }`}
                    >
                      <option value="">
                        {t('mapping.table.ignore_column')}
                      </option>
                      {SELLER_FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {sellerFieldLabel(t, f.key)}
                          {f.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-neut-sec">
        <span>
          {ignoredCount > 0
            ? t('mapping.columns_ignored', { count: ignoredCount })
            : t('mapping.all_mapped')}
        </span>
        <button
          type="button"
          onClick={() => {
            // Re-derive from similarity, ignoring the remembered mapping for this file shape
            // (without deleting the organization's stored mapping).
            const next = seededMap(file, {}, proposed);
            setMapping(next.mapping);
            setFromMemory(next.fromMemory);
          }}
          className="underline hover:text-neut-prim"
        >
          {t('mapping.reset_learned')}
        </button>
      </div>

      {!canContinue && (
        <p className="rounded-lg bg-error-50 px-3 py-2 text-sm text-error-600">
          {t('mapping.missing_required', {
            fields: missingRequired
              .map((f) => sellerFieldLabel(t, f.key))
              .join(', '),
          })}
        </p>
      )}

      <div className="flex items-center gap-3 border-t border-neut-border pt-4">
        <Button variant="secondary" size="sm" width="auto" onClick={onBack}>
          {t('mapping.back')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          width="auto"
          disabled={!canContinue}
          onClick={() => {
            // Best-effort persist for the organization; navigation isn't blocked on the request.
            rememberMapping.mutate({
              headerSignature: headerSignature(file.headers),
              mapping,
            });
            onConfirm(mapping);
          }}
        >
          {t('mapping.continue')}
        </Button>
      </div>
    </div>
  );
}
